import * as Sentry from "@sentry/react";
import { getStoredPreferences } from "./cookies";

// Initialize Sentry for the client-side
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    console.warn('[Sentry] VITE_SENTRY_DSN not set, error tracking disabled');
    return;
  }

  // Check cookie consent - Sentry falls under analytics category
  const preferences = getStoredPreferences();
  const hasAnalyticsConsent = preferences?.analytics ?? false;

  // If user has explicitly declined analytics, don't initialize Sentry session replay
  // We still initialize basic error tracking as it's considered necessary for site operation
  const enableSessionReplay = hasAnalyticsConsent;

  const release = import.meta.env.VITE_SENTRY_RELEASE || `lifeitself@${import.meta.env.VITE_APP_VERSION || '1.0.0'}`;
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || (import.meta.env.PROD ? 'production' : 'development');

  Sentry.init({
    dsn,
    release,
    environment,

    // Performance monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Session replay for debugging - only enabled with analytics consent
    replaysSessionSampleRate: enableSessionReplay ? 0.1 : 0,
    replaysOnErrorSampleRate: enableSessionReplay ? 1.0 : 0,

    // Integrations for enhanced tracking
    integrations: [
      // Breadcrumbs are enabled by default, but we configure them
      Sentry.breadcrumbsIntegration({
        console: true,
        dom: true,
        fetch: true,
        history: true,
        xhr: true,
      }),

      // HTTP client errors
      Sentry.httpClientIntegration(),

      // React Router integration for wouter
      Sentry.browserTracingIntegration({
        enableInp: true,
      }),

      // Replay integration for session recording - only if consent given
      ...(enableSessionReplay ? [Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        maskAllInputs: true,
      })] : []),
    ],

    // Error grouping rules - customize fingerprinting
    beforeSend(event, hint) {
      const error = hint.originalException;

      // Add custom fingerprinting for common error patterns
      if (error instanceof Error) {
        // Group network errors together
        if (error.message.includes('fetch') || error.message.includes('network')) {
          event.fingerprint = ['network-error', error.message.split(':')[0]];
        }

        // Group Stripe-related errors
        if (error.message.includes('stripe') || error.message.includes('Stripe')) {
          event.fingerprint = ['stripe-error', error.message.split(':')[0]];
        }

        // Group API errors by endpoint
        const apiMatch = error.message.match(/\/api\/([^\/\s]+)/);
        if (apiMatch) {
          event.fingerprint = ['api-error', apiMatch[1]];
        }
      }

      // Filter out known non-critical errors in development
      if (!import.meta.env.PROD) {
        // Don't send HMR errors
        if (event.message?.includes('HMR') || event.message?.includes('hot reload')) {
          return null;
        }
      }

      return event;
    },

    // Add extra context to all events
    initialScope: {
      tags: {
        app: 'lifeitself-client',
        platform: 'web',
      },
    },

    // Ignore common benign errors
    ignoreErrors: [
      // Browser extensions
      /^chrome-extension/,
      /^moz-extension/,
      // Common browser errors
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Network errors that are expected
      'Failed to fetch',
      'NetworkError',
      'AbortError',
      // User navigation
      'Navigation cancelled',
    ],

    // Capture unhandled promise rejections
    attachStacktrace: true,

    // Normalize stack trace paths
    normalizeDepth: 10,
  });

  console.log(`[Sentry] Initialized for ${environment} environment`);
}

// Track user context when available
export function setUserContext(user: { id: string; email?: string; name?: string }) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.name,
  });
}

// Clear user context on logout
export function clearUserContext() {
  Sentry.setUser(null);
}

// Add custom breadcrumb for important user actions
export function addBreadcrumb(
  category: string,
  message: string,
  data?: Record<string, unknown>,
  level: Sentry.SeverityLevel = 'info'
) {
  Sentry.addBreadcrumb({
    category,
    message,
    data,
    level,
    timestamp: Date.now() / 1000,
  });
}

// Capture custom error with context
export function captureError(error: Error, context?: Record<string, unknown>) {
  Sentry.withScope((scope) => {
    if (context) {
      scope.setExtras(context);
    }
    Sentry.captureException(error);
  });
}

// Capture custom message
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info') {
  Sentry.captureMessage(message, level);
}

// Start a performance transaction
export function startTransaction(name: string, op: string) {
  return Sentry.startInactiveSpan({
    name,
    op,
  });
}

// Tag the current scope
export function setTag(key: string, value: string) {
  Sentry.setTag(key, value);
}

// Set extra context data
export function setExtra(key: string, value: unknown) {
  Sentry.setExtra(key, value);
}

// Export Sentry for direct access if needed
export { Sentry };
